import "./ClientMusculoskeletalHistoryFormComponent.scss";
import CardComponent from "../../../../shared/components/card/CardComponent";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {Misc} from "../../../../constants";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import FormikRadioButtonGroupComponent
    from "../../../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";
import {IClientMusculoskeletalHistoryForm} from "../../../../shared/models/client.model";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {IMusculoskeletalHistoryOption} from "../../../../shared/models/common.model";
import FormikTextAreaComponent
    from "../../../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import LoaderComponent from "../../../../shared/components/loader/LoaderComponent";
import StatusCardComponent from "../../../../shared/components/status-card/StatusCardComponent";
import HeadingWithNavigationComponent
    from "../../../../shared/components/heading-with-navigation/HeadingWithNavigationComponent";
import DetailsViewCardComponent from "../../../../shared/components/details-view-card/DetailsViewCardComponent";
import ModalComponent from "../../../../shared/components/modal/ModalComponent";
import AcknowledgementFormComponent from "../../acknowledgement-form/AcknowledmentFormComponent";

interface ClientMusculoskeletalFormComponentProps {
    clientMedicalHistoryDetails: any;
    isClientMedicalHistoryDetailsLoaded: boolean | undefined;
    isClientMedicalHistoryDetailsLoading: boolean | undefined;
    isClientMedicalHistoryDetailsLoadingFailed: boolean | undefined;
    onCancel: () => void;
    clientId: string;
    onNext?: () => void;
    onSave: (clientMusculoskeletalHistory: any) => void;
}

const ClientMusculoskeletalHistoryFormValidationSchema = Yup.object({
    musculoskeletal_history: Yup.object().test(
        "musculoskeletal-history-validation",
        "Required field when 'Yes' is selected",
        (value) => {
            if (!value) return false;
            return Object.values(value).every((item: any) => {
                if (item?.value === "Yes") {
                    return item?.text && item.text.trim() !== "";
                }
                return true;
            });
        }
    ),
});

const ClientMusculoskeletalHistoryFormInitialValues: IClientMusculoskeletalHistoryForm = {
    "musculoskeletal_history": {}
};

const ClientMusculoskeletalHistoryFormComponent = (props: ClientMusculoskeletalFormComponentProps) => {

    const {
        onCancel, onSave,
        clientId,
        clientMedicalHistoryDetails,
        isClientMedicalHistoryDetailsLoaded,
        isClientMedicalHistoryDetailsLoading,
        isClientMedicalHistoryDetailsLoadingFailed
    } = props;
    const [clientMusculoskeletalHistoryFormInitialValues, setClientMusculoskeletalHistoryFormInitialValues] = useState<IClientMusculoskeletalHistoryForm>(_.cloneDeep(ClientMusculoskeletalHistoryFormInitialValues));
    const [isClientMusculoskeletalHistorySavingInProgress, setIsClientMusculoskeletalHistorySavingInProgress] = useState(false);
    const {musculoskeletalHistoryOptionsList} = useSelector((state: IRootReducerState) => state.staticData);
    const dispatch = useDispatch();
    const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] = useState(false);

    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        setClientMusculoskeletalHistoryFormInitialValues(values);
        setIsAcknowledgementModalOpen(true);
    }, [])

    const handleAcknowledgementFormSubmit = useCallback((acknowledgementValues:any) => {
        const payload = {
            ...clientMusculoskeletalHistoryFormInitialValues,
            ...acknowledgementValues,
            update_type: "profile",
            client_id: clientId,
        };
        setIsClientMusculoskeletalHistorySavingInProgress(true);
        CommonService._client.ClientMedicalHistoryMusculoskeletalHistoryDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<IClientMusculoskeletalHistoryForm>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setIsClientMusculoskeletalHistorySavingInProgress(false);
                setClientMusculoskeletalHistoryFormInitialValues(_.cloneDeep(clientMusculoskeletalHistoryFormInitialValues));
                onSave(response);
            })
            .catch((error: any) => {
                CommonService.handleErrors(error[Misc.API_RESPONSE_ERROR_KEY], error);
                setIsClientMusculoskeletalHistorySavingInProgress(false);
            })
    }, [onSave,clientId,clientMusculoskeletalHistoryFormInitialValues]);

    useEffect(() => {
        if (clientMedicalHistoryDetails) {
            setClientMusculoskeletalHistoryFormInitialValues({
                musculoskeletal_history: clientMedicalHistoryDetails?.musculoskeletal_history
            });
        }
    }, [dispatch, clientMedicalHistoryDetails]);

    const isAllQuestionsAnswered = (values: any) => {
        const answeredQuestions = Object.values(values.musculoskeletal_history);
        const answeredYesNoQuestions = answeredQuestions.filter((question: any) => question.value === "Yes" || question.value === 'No');
        return answeredYesNoQuestions.length === musculoskeletalHistoryOptionsList.length;
    };


    return (
        <div className={'client-musculoskeletal-form-component'}>
            <>
                {
                    isClientMedicalHistoryDetailsLoading && <div>
                        <LoaderComponent/>
                    </div>
                }
                {
                    isClientMedicalHistoryDetailsLoadingFailed &&
                    <StatusCardComponent title={"Failed to fetch medical Details"}/>
                }
            </>
            {
                isClientMedicalHistoryDetailsLoaded && <>
                    <HeadingWithNavigationComponent onBackNavigation={onCancel}
                                                    title={"Back"}/>
                    <Formik
                        validationSchema={ClientMusculoskeletalHistoryFormValidationSchema}
                        initialValues={clientMusculoskeletalHistoryFormInitialValues}
                        onSubmit={onSubmit}
                        validateOnChange={false}
                        validateOnBlur={true}
                        enableReinitialize={true}
                        validateOnMount={true}>
                        {({values, setFieldValue, isValid,errors, validateForm}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (<CardComponent>
                                    <div className={'edit-musculoskeletal-history-heading'}>
                                        Edit Musculoskeletal History
                                    </div>
                                    <Form noValidate={true} className={"t-form"}>
                                        <div className={"edit-musculoskeletal-history-form-wrapper"}>
                                            <DetailsViewCardComponent title={"Musculoskeletal History"}>
                                                <div className={'edit-musculoskeletal-history-sub-heading'}>
                                                    Have you ever had:
                                                </div>

                                                {
                                                    musculoskeletalHistoryOptionsList?.map((question: IMusculoskeletalHistoryOption) => {
                                                        const {_id, client_title, placeholder} = question;
                                                        return <>
                                                            <div className="ts-row mrg-top-10 mrg-bottom-10"
                                                                 key={_id}>
                                                                <div className="ts-col-md-4">
                                                                    <div className={"mrg-bottom-10"}>
                                                                        {client_title} *
                                                                    </div>
                                                                    <Field name={`musculoskeletal_history.${_id}.value`}>
                                                                        {
                                                                            (field: FieldProps) => (
                                                                                <FormikRadioButtonGroupComponent
                                                                                    options={CommonService._staticData.yesNoOptions}
                                                                                    displayWith={(option) => option.title}
                                                                                    valueExtractor={(option) => option.title}
                                                                                    required={true}
                                                                                    formikField={field}
                                                                                    id={"musculos"}
                                                                                    onChange={(value) => {
                                                                                        if (value === "No") {
                                                                                            setFieldValue(`musculoskeletal_history.${_id}.text`, undefined);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            )
                                                                        }
                                                                    </Field>
                                                                </div>
                                                                <div className={"ts-col-md-8"}>
                                                                    {
                                                                        values?.musculoskeletal_history[_id]?.value === 'Yes' &&
                                                                        <Field name={`musculoskeletal_history.${_id}.text`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikTextAreaComponent
                                                                                        id={"musculos_input"}
                                                                                        placeholder={placeholder}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                        rows={"3"}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </>
                                                    })
                                                }
                                            </DetailsViewCardComponent>
                                        </div>
                                        <div className="t-form-actions mrg-bottom-0 mrg-top-25">
                                            <ButtonComponent
                                                id={"home_btn"}
                                                variant={"outlined"}
                                                onClick={onCancel}
                                                disabled={isClientMusculoskeletalHistorySavingInProgress}
                                                color={"accent"}
                                            >
                                                Cancel
                                            </ButtonComponent>&nbsp;
                                            <ButtonComponent
                                                id={"save_next_btn"}
                                                className={'mrg-left-25'}
                                                isLoading={isClientMusculoskeletalHistorySavingInProgress}
                                                disabled={isClientMusculoskeletalHistorySavingInProgress || !isValid || !isAllQuestionsAnswered(values)}
                                                type={"submit"}
                                                color={"accent"}
                                            >
                                                Next
                                            </ButtonComponent>
                                        </div>
                                    </Form>
                                </CardComponent>
                            )
                        }}
                    </Formik>
                </>
            }
            <ModalComponent isOpen={isAcknowledgementModalOpen}>
                <AcknowledgementFormComponent setIsAcknowledgementModalOpen={setIsAcknowledgementModalOpen}
                                              isLoading={isClientMusculoskeletalHistorySavingInProgress}
                                              onSubmit={handleAcknowledgementFormSubmit}/>
            </ModalComponent>
        </div>
    );

};

export default ClientMusculoskeletalHistoryFormComponent;
